import { supabase } from './supabase';

// Donors
export async function fetchDonors(userId) {
  if (!supabase || !userId) return [];

  const { data, error } = await supabase
    .from('donors')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching donors:', error);
    return [];
  }

  // Deduplicate by name, preferring records with meeting/task data
  const seen = {};
  const deduped = [];
  for (const donor of (data || [])) {
    if (!seen[donor.name]) {
      seen[donor.name] = donor;
      deduped.push(donor);
    } else {
      // If new one has meeting/task and existing doesn't, replace
      const existing = seen[donor.name];
      if ((donor.next_meeting && !existing.next_meeting) ||
          (donor.next_task && !existing.next_task)) {
        const idx = deduped.findIndex(d => d.name === donor.name);
        if (idx !== -1) {
          deduped[idx] = donor;
          seen[donor.name] = donor;
        }
      }
    }
  }
  return deduped;
}

export async function createDonor(userId, donor) {
  console.log('[CLIENT createDonor] Called with:', { userId, donorName: donor?.name });
  console.trace('[CLIENT createDonor] Stack trace:');

  if (!supabase) {
    console.error('createDonor: supabase client is null');
    return null;
  }
  if (!userId) {
    console.error('createDonor: userId is missing');
    return null;
  }

  // Check if donor already exists (server may have created it)
  const { data: existing } = await supabase
    .from('donors')
    .select('id, next_meeting, next_task')
    .eq('user_id', userId)
    .eq('name', donor.name)
    .limit(1);

  if (existing && existing.length > 0) {
    // Donor exists - only update if we have new meeting/task data
    const existingDonor = existing[0];
    const updates = {};

    // Only update meeting if we have new data and existing doesn't
    if (donor.nextMeeting && donor.nextMeeting !== 'Not scheduled' && !existingDonor.next_meeting) {
      updates.next_meeting = donor.nextMeeting;
    }
    if (donor.nextTask && donor.nextTask !== 'No tasks' && !existingDonor.next_task) {
      updates.next_task = donor.nextTask;
    }
    // Always update narrative if provided
    if (donor.narrative) {
      updates.narrative = donor.narrative;
    }

    if (Object.keys(updates).length > 0) {
      const { data, error } = await supabase
        .from('donors')
        .update(updates)
        .eq('id', existingDonor.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating existing donor:', error);
      } else {
        console.log('createDonor: Updated existing donor:', donor.name);
      }
      return data;
    }
    console.log('createDonor: Donor already exists, skipping:', donor.name);
    return existingDonor;
  }

  const insertData = {
    user_id: userId,
    name: donor.name,
    narrative: donor.narrative || '',
    last_contact: donor.lastContact || null,
    amount: donor.amount || 'Unknown',
    sentiment: donor.sentiment || null,
    next_meeting: donor.nextMeeting || null,
    next_task: donor.nextTask || null,
    group_id: donor.groupId || null
  };

  console.log('createDonor inserting:', insertData);

  const { data, error } = await supabase
    .from('donors')
    .insert([insertData])
    .select()
    .single();

  if (error) {
    console.error('Error creating donor:', error.message, error.details, error.hint, error);
    return null;
  }
  console.log('createDonor success:', data);
  return data;
}

export async function updateDonor(donorId, updates) {
  if (!supabase || !donorId) return null;

  const { data, error } = await supabase
    .from('donors')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', donorId)
    .select()
    .single();

  if (error) {
    console.error('Error updating donor:', error);
    return null;
  }
  return data;
}

// Update donor by name (looks up ID first)
// If appendNarrative is true, notes will be appended to existing narrative
export async function updateDonorByName(userId, donorName, updates, appendNarrative = true) {
  if (!supabase || !userId || !donorName) return null;

  // First find the donor by name (include narrative if we need to append)
  const { data: donors, error: findError } = await supabase
    .from('donors')
    .select('id, narrative')
    .eq('user_id', userId)
    .eq('name', donorName)
    .limit(1);

  if (findError || !donors || donors.length === 0) {
    console.error('Could not find donor to update:', donorName, findError);
    return null;
  }

  const donor = donors[0];
  const finalUpdates = { ...updates };

  // If we're appending a note to narrative, combine with existing
  // But first check if the note already exists (deduplication)
  if (appendNarrative && updates.narrative) {
    const existingNarrative = donor.narrative || '';
    const newNote = updates.narrative.trim();

    // Check if this exact note already exists in the narrative
    if (existingNarrative.includes(newNote)) {
      console.log('Note already exists in narrative, skipping duplicate:', newNote.substring(0, 50));
      delete finalUpdates.narrative; // Don't update narrative if it's a duplicate
    } else if (existingNarrative) {
      finalUpdates.narrative = existingNarrative + '\n\n' + newNote;
    }
  }

  // Clear timeline cache if narrative is being updated
  if (finalUpdates.narrative) {
    finalUpdates.timeline_events = null;
  }

  // Only update if there are actual changes
  if (Object.keys(finalUpdates).length === 0) {
    console.log('No updates to apply after deduplication');
    return donor;
  }

  return updateDonor(donor.id, finalUpdates);
}

// Delete donor by name (looks up ID first)
export async function deleteDonorByName(userId, donorName) {
  if (!supabase || !userId || !donorName) return false;

  // First find the donor by name
  const { data: donors, error: findError } = await supabase
    .from('donors')
    .select('id')
    .eq('user_id', userId)
    .eq('name', donorName)
    .limit(1);

  if (findError || !donors || donors.length === 0) {
    console.error('Could not find donor to delete:', donorName, findError);
    return false;
  }

  const donorId = donors[0].id;
  return deleteDonor(donorId);
}

export async function deleteDonor(donorId) {
  if (!supabase || !donorId) return false;

  const { error } = await supabase
    .from('donors')
    .delete()
    .eq('id', donorId);

  if (error) {
    console.error('Error deleting donor:', error);
    return false;
  }
  return true;
}

// Notes
export async function fetchNotes(userId, donorId = null) {
  if (!supabase || !userId) return [];

  let query = supabase
    .from('notes')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (donorId) {
    query = query.eq('donor_id', donorId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching notes:', error);
    return [];
  }
  return data || [];
}

export async function createNote(userId, donorId, content) {
  if (!supabase || !userId) return null;

  const { data, error } = await supabase
    .from('notes')
    .insert([{
      user_id: userId,
      donor_id: donorId,
      content: content
    }])
    .select()
    .single();

  if (error) {
    console.error('Error creating note:', error);
    return null;
  }
  return data;
}

// Lists
export async function fetchLists(userId) {
  if (!supabase || !userId) return [];

  const { data, error } = await supabase
    .from('lists')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching lists:', error);
    return [];
  }
  return data || [];
}

export async function createList(userId, name, groups = [], organizationPrompt = '', groupId = null, options = {}) {
  if (!supabase || !userId) return null;

  const insertData = {
    user_id: userId,
    name: name,
    groups: groups,
    organization_prompt: organizationPrompt,
    group_id: groupId
  };

  // Add table-specific fields if this is a table view
  if (options.type === 'table') {
    insertData.type = 'table';
    insertData.columns = options.columns || [];
    insertData.rows = options.rows || [];
    insertData.column_prompt = options.columnPrompt || '';
  }

  const { data, error } = await supabase
    .from('lists')
    .insert([insertData])
    .select()
    .single();

  if (error) {
    console.error('Error creating list:', error);
    return null;
  }
  return data;
}

export async function updateList(listId, updates) {
  if (!supabase || !listId) return null;

  const { data, error } = await supabase
    .from('lists')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', listId)
    .select()
    .single();

  if (error) {
    console.error('Error updating list:', error);
    return null;
  }
  return data;
}

export async function deleteList(listId) {
  if (!supabase || !listId) return false;

  const { error } = await supabase
    .from('lists')
    .delete()
    .eq('id', listId);

  if (error) {
    console.error('Error deleting list:', error);
    return false;
  }
  return true;
}

// Wipe all data for a user
export async function wipeAllUserData(userId) {
  if (!supabase || !userId) return false;

  try {
    // Delete all notes for this user
    const { error: notesError } = await supabase
      .from('notes')
      .delete()
      .eq('user_id', userId);
    if (notesError) console.error('Error deleting notes:', notesError);

    // Delete all lists for this user
    const { error: listsError } = await supabase
      .from('lists')
      .delete()
      .eq('user_id', userId);
    if (listsError) console.error('Error deleting lists:', listsError);

    // Delete all donors for this user
    const { error: donorsError } = await supabase
      .from('donors')
      .delete()
      .eq('user_id', userId);
    if (donorsError) console.error('Error deleting donors:', donorsError);

    console.log('Wiped all data for user:', userId);
    return true;
  } catch (error) {
    console.error('Error wiping user data:', error);
    return false;
  }
}

// Helper to convert Supabase donor format to app format
export function supabaseDonorToAppFormat(donor) {
  return {
    id: donor.id,
    name: donor.name,
    narrative: donor.narrative || '',
    lastContact: donor.last_contact || 'Unknown',
    amount: donor.amount || 'Unknown',
    sentiment: donor.sentiment || null,
    groupId: donor.group_id || null,
    group_ids: donor.group_ids || [], // Multi-group support
    nextTask: donor.next_task || null,
    nextMeeting: donor.next_meeting || null,
    insightMessages: donor.insight_messages || [],
    timelineEvents: donor.timeline_events || [],
    keyDetails: donor.key_details || [],
    tasks: donor.tasks || [],
    // Contact info fields
    email: donor.email || '',
    phone: donor.phone || '',
    address: donor.address || '',
    organization: donor.organization || '',
    title: donor.title || ''
  };
}

// Convert donors array to object format (keyed by name)
export function donorsArrayToObject(donors) {
  const obj = {};
  donors.forEach(donor => {
    const formatted = supabaseDonorToAppFormat(donor);
    obj[formatted.name] = formatted;
  });
  return obj;
}

// Helper to convert Supabase list format to app format
export function supabaseListToAppFormat(list) {
  const formatted = {
    id: list.id,
    name: list.name,
    organizationPrompt: list.organization_prompt || list.name,
    groups: list.groups || [],
    groupId: list.group_id || null,
    createdAt: list.created_at,
    lastEvaluated: list.last_evaluated || null
  };

  // Add table-specific fields if this is a table view
  if (list.type === 'table') {
    formatted.type = 'table';
    formatted.columns = list.columns || [];
    formatted.rows = list.rows || [];
    formatted.columnPrompt = list.column_prompt || '';
  }

  return formatted;
}

// Groups (for categorizing people)
export async function fetchGroups(userId) {
  if (!supabase || !userId) return [];

  const { data, error } = await supabase
    .from('groups')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching groups:', error);
    return [];
  }
  return data || [];
}

export async function createGroup(userId, name, color = '#8b5cf6', context = null) {
  if (!supabase || !userId) return null;

  const insertData = {
    user_id: userId,
    name: name,
    color: color
  };
  if (context) insertData.context = context;

  const { data, error } = await supabase
    .from('groups')
    .insert([insertData])
    .select()
    .single();

  if (error) {
    console.error('Error creating group:', error);
    return null;
  }
  return data;
}

export async function updateGroup(groupId, updates) {
  if (!supabase || !groupId) return null;

  const { data, error } = await supabase
    .from('groups')
    .update(updates)
    .eq('id', groupId)
    .select()
    .single();

  if (error) {
    console.error('Error updating group:', error);
    return null;
  }
  return data;
}

export async function deleteGroup(groupId) {
  if (!supabase || !groupId) return false;

  // First, set group_id to null for all donors in this group
  const { error: updateError } = await supabase
    .from('donors')
    .update({ group_id: null })
    .eq('group_id', groupId);

  if (updateError) {
    console.error('Error clearing donors from group:', updateError);
  }

  // Then delete the group
  const { error } = await supabase
    .from('groups')
    .delete()
    .eq('id', groupId);

  if (error) {
    console.error('Error deleting group:', error);
    return false;
  }
  return true;
}

// Helper to convert Supabase group format to app format
export function supabaseGroupToAppFormat(group) {
  return {
    id: group.id,
    name: group.name,
    color: group.color || '#8b5cf6',
    context: group.context || null,
    createdAt: group.created_at
  };
}

// Subscriptions
export async function fetchSubscription(userId) {
  if (!supabase || !userId) return null;

  const { data, error } = await supabase
    .from('subscriptions')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (error) {
    // No subscription found is not an error for new users
    if (error.code === 'PGRST116') {
      console.log('No subscription found for user:', userId);
      return null;
    }
    console.error('Error fetching subscription:', error);
    return null;
  }
  return data;
}

export async function createSubscription(userId, plan = 'trial') {
  console.log('createSubscription called with userId:', userId, 'plan:', plan);

  if (!supabase) {
    console.error('createSubscription: supabase client not available');
    throw new Error('Database not available');
  }

  if (!userId) {
    console.error('createSubscription: userId is missing');
    throw new Error('User ID required');
  }

  const subscriptionData = {
    user_id: userId,
    plan: plan,
    status: 'active'
  };

  // Set trial end date for trial plan (7 days)
  if (plan === 'trial') {
    const trialEnd = new Date();
    trialEnd.setDate(trialEnd.getDate() + 7);
    subscriptionData.trial_ends_at = trialEnd.toISOString();
  }

  // Set donor limit for free plan
  if (plan === 'free') {
    subscriptionData.donor_limit = 20;
  }

  console.log('Attempting to insert subscription:', subscriptionData);

  const { data, error } = await supabase
    .from('subscriptions')
    .insert([subscriptionData])
    .select()
    .single();

  if (error) {
    console.error('Error creating subscription:', error);
    throw new Error(error.message || 'Failed to create subscription');
  }

  console.log('Subscription created successfully:', data);
  return data;
}

export async function updateSubscription(userId, updates) {
  if (!supabase || !userId) return null;

  const { data, error } = await supabase
    .from('subscriptions')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('user_id', userId)
    .select()
    .single();

  if (error) {
    console.error('Error updating subscription:', error);
    return null;
  }
  return data;
}

// Get donor count for a user
export async function getDonorCount(userId) {
  if (!supabase || !userId) return 0;

  const { count, error } = await supabase
    .from('donors')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId);

  if (error) {
    console.error('Error counting donors:', error);
    return 0;
  }
  return count || 0;
}

// Check if user can add more donors based on their plan
export async function canAddDonor(userId) {
  if (!supabase || !userId) return { canAdd: false, count: 0, limit: 0 };

  const [donorCount, subscription] = await Promise.all([
    getDonorCount(userId),
    fetchSubscription(userId)
  ]);

  // No subscription means trial or new user - allow unlimited during trial
  if (!subscription) {
    return { canAdd: true, count: donorCount, limit: null, plan: 'trial' };
  }

  // Trial plan - unlimited (check if not expired)
  if (subscription.plan === 'trial') {
    const trialEnd = new Date(subscription.trial_ends_at);
    if (trialEnd > new Date()) {
      return { canAdd: true, count: donorCount, limit: null, plan: 'trial' };
    }
    // Trial expired - treat as free
    return { canAdd: donorCount < 20, count: donorCount, limit: 20, plan: 'expired_trial' };
  }

  // Pro plan - unlimited
  if (subscription.plan === 'pro') {
    return { canAdd: true, count: donorCount, limit: null, plan: 'pro' };
  }

  // Free plan - 20 limit
  const limit = subscription.donor_limit || 20;
  return { canAdd: donorCount < limit, count: donorCount, limit, plan: 'free' };
}

// Chat Messages
export async function fetchChatHistory(userId) {
  if (!supabase || !userId) return [];

  const { data, error } = await supabase
    .from('chat_messages')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching chat history:', error);
    return [];
  }

  // Convert to app format
  return (data || []).map(msg => ({
    role: msg.role,
    content: msg.content,
    action: msg.action,
    messageType: msg.message_type,
    timestamp: msg.created_at
  }));
}

export async function saveChatMessage(userId, message) {
  if (!supabase || !userId) return null;

  const { data, error } = await supabase
    .from('chat_messages')
    .insert([{
      user_id: userId,
      role: message.role,
      content: message.content,
      action: message.action || null,
      message_type: message.messageType || null
    }])
    .select()
    .single();

  if (error) {
    console.error('Error saving chat message:', error);
    return null;
  }
  return data;
}

export async function clearChatHistory(userId) {
  if (!supabase || !userId) return false;

  const { error } = await supabase
    .from('chat_messages')
    .delete()
    .eq('user_id', userId);

  if (error) {
    console.error('Error clearing chat history:', error);
    return false;
  }
  return true;
}
